import React from 'react';
import { CrmFxRates } from '../../services/studio-crm';
import { formatCurrencyTotals, formatMoney, toEur, CurrencyTotals, AttributionSplit } from './crm-totals';

export const CARD = 'flex flex-col bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 shadow-sm min-w-[260px] flex-1';
export const COMPACT_CARD = 'flex flex-col bg-white border border-[#e2e8f0] rounded-xl px-3 py-3 shadow-sm min-w-0 flex-1';
export const COMPACT_LABEL = 'text-[11px] font-medium text-slate-500 uppercase tracking-wide leading-tight';
export const CARD_LABEL = 'text-xs font-medium text-slate-500 uppercase tracking-wider';

// One headline per card: the ≈ EUR total when every currency present has a
// reporting rate, otherwise the per-currency figures. Sourced vs influenced is
// a split bar inside the card (by EUR value when convertible, else by count).
export default function MoneyCard({ label, totals, split, rates, footer, compact = false }: {
  label: string;
  // Narrow card for a single row of figures (Studio TV): no minimum width, smaller headline
  compact?: boolean;
  totals: CurrencyTotals;
  split: AttributionSplit;
  rates: CrmFxRates['rates'] | null;
  footer: string;
}) {
  const eur = toEur(totals, rates);
  const currencies = Object.keys(totals).filter(c => (totals as any)[c] > 0);
  const onlyEur = currencies.length === 1 && currencies[0] === 'EUR';

  const sourcedEur = toEur(split.sourced.value, rates);
  const influencedEur = toEur(split.influenced.value, rates);
  const byValue = sourcedEur !== null && influencedEur !== null && sourcedEur + influencedEur > 0;
  const sourced = byValue ? sourcedEur! : split.sourced.count;
  const influenced = byValue ? influencedEur! : split.influenced.count;
  const sourcedPct = sourced + influenced > 0 ? Math.round((sourced / (sourced + influenced)) * 100) : 0;

  return (
    <div className={compact ? COMPACT_CARD : CARD}>
      <span className={compact ? COMPACT_LABEL : CARD_LABEL}>{label}</span>
      <span className={`${compact ? 'text-xl' : 'text-2xl'} font-bold text-navy mt-1`}>
        {eur !== null ? `${onlyEur || currencies.length === 0 ? '' : '≈ '}${formatMoney(eur, 'EUR', true)}` : formatCurrencyTotals(totals)}
      </span>
      <span className="text-[11px] text-slate-400 h-4">
        {eur !== null && !onlyEur && currencies.length > 0 ? formatCurrencyTotals(totals) : eur === null ? 'Set exchange rates for a EUR total' : ''}
      </span>
      <div className="mt-2">
        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden flex">
          <div className="h-full bg-primary" style={{ width: `${sourcedPct}%` }} />
        </div>
        <div className="flex justify-between text-[11px] text-slate-500 mt-1">
          <span><span className="inline-block w-2 h-2 rounded-full bg-primary mr-1" />{split.sourced.count} sourced</span>
          <span>{split.influenced.count} influenced<span className="inline-block w-2 h-2 rounded-full bg-slate-200 ml-1" /></span>
        </div>
      </div>
      <span className="text-xs text-slate-500 mt-1.5">{footer}</span>
    </div>
  );
}
